import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import { LevelNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.level.not-found";
import { NewMorphemeLessonLayoutContextProvider } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._context";
import type { MorphemeMnemonicsViewNavigationRefValue } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import type { GraphemeMorphemeMnemonicsPanelMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  getGraphemeLessonPart,
  type GraphemeLessonPart,
  type Level,
} from "@-/dev.improve.haniverse.lib.level";
import { useMorphemeMnemonicsPanelMorphemeSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { useFindGraphemeLessonLevel } from "@-/dev.improve.haniverse.routes.nav.app._lib.level.read";
import {
  usePrefetchedSuspenseQuery,
  type DataTag,
  type QueryKey,
} from "@-/com.npmjs.tanstack__react-query";
import { type ReactNode, useRef, useState } from "react";

/** The lesson card's providers, below the gate: the slice here is non-null by construction. */
function MorphemeLessonLayoutProviders({
  children,
  grapheme,
  lesson,
  lessonPart,
  onlyPinyin,
  slice,
  sliceQueryKey,
}: {
  children: ReactNode;
  grapheme: string;
  lesson: Level;
  lessonPart: GraphemeLessonPart;
  onlyPinyin?: string;
  slice: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  sliceQueryKey: DataTag<QueryKey, GraphemeMorphemeMnemonicsPanelMorphemeSlice | null, Error>;
}) {
  const resolvedSlice = useResolvedQuery(sliceQueryKey, slice);
  const [activeIndex, setActiveIndex] = useState(0);
  const viewNavigationRef = useRef<MorphemeMnemonicsViewNavigationRefValue | null>(null);

  return (
    <NewMorphemeLessonLayoutContextProvider
      activeIndex={activeIndex}
      grapheme={grapheme}
      lesson={lesson}
      lessonPart={lessonPart}
      morphemeMnemonicsPanelMorphemeSlice={resolvedSlice}
      onlyPinyin={onlyPinyin}
      setActiveIndex={setActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      {children}
    </NewMorphemeLessonLayoutContextProvider>
  );
}

/**
 * The lesson card's gate: the ONE observer of the grapheme's mnemonics slice for this card. Renders
 * the level / morpheme not-found states, then publishes the narrowed slice to the lesson context.
 */
export function MorphemeLessonLayoutSuspense({
  children,
  grapheme,
  part,
  pinyin,
}: {
  children: ReactNode;
  grapheme: string;
  /** A pronunciation-level lesson (Level `2-P1`) rather than the character's own lesson. */
  part?: GraphemeLessonPart;
  /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
  pinyin?: string;
}) {
  const morphemeMnemonicsPanelCardQueryOptions =
    useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(grapheme);
  const { data } = usePrefetchedSuspenseQuery(morphemeMnemonicsPanelCardQueryOptions);

  const level = useFindGraphemeLessonLevel({ grapheme, part });

  if (level === undefined) {
    return <LevelNotFound character={grapheme} />;
  }

  if (grapheme === "" || data === null) {
    return <MorphemeNotFound morpheme={grapheme} />;
  }

  return (
    <MorphemeLessonLayoutProviders
      grapheme={grapheme}
      lesson={level}
      lessonPart={
        part ??
        getGraphemeLessonPart({
          grapheme,
          isPronunciationLesson: part === "pronunciation",
          level,
        })
      }
      onlyPinyin={pinyin}
      slice={data}
      sliceQueryKey={morphemeMnemonicsPanelCardQueryOptions.queryKey}
    >
      {children}
    </MorphemeLessonLayoutProviders>
  );
}
